import { zodResolver } from '@hookform/resolvers/zod';
import i18next from 'i18next';
import { useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { Card } from '#pie/components/card/Card';
import { Divider } from '#pie/components/divider/Divider';
import { ErrorMessage } from '#pie/components/form/errorMaps';
import { Page, PageHeader } from '#pie/components/page/Page';
import { Stack } from '#pie/components/stack/Stack';
import { Text } from '#pie/components/text/Text';
import { getConnectionInformation, Product } from '#pie/internal/api/pie-bff';
import { isValidEan } from '#pie/utils/isValidEan';
import { typedFormFields } from '#pie/utils/typedFormFields';
import type {
  PartialNonConformitySchema,
  StepProps,
} from './TicketCreateNonConformity';
import { useNonConformityFormContext } from './TicketCreateNonConformity';

export const codesSchema = z.object({
  electricityEan: z
    .string()
    .length(18, {
      error: () => ({
        message: 'EAN-18 moet 18 karakters lang zijn',
      }),
    })
    .refine((value) => isValidEan(value, { optional: true }), {
      error: () => ({
        message: i18next.t(ErrorMessage.ean18),
      }),
    })
    .optional(),
});

type CodesForm = z.infer<typeof codesSchema>;

const { FormText } = typedFormFields<CodesForm>();

export const TicketCreateNonConformityCodes = ({
  wizardActions,
  onSubmit,
  stepper,
}: StepProps) => {
  const { t } = useTranslation();
  const { t: transtic } = useTranslation('ticket');
  const { data, setStepShouldBlock, setIsLoading } =
    useNonConformityFormContext();

  const refinedSchema = codesSchema.refine((check) => check.electricityEan, {
    error: () => ({
      message: i18next.t(ErrorMessage.either),
    }),
    path: ['electricityEan'],
  });
  const resolver = zodResolver(refinedSchema);

  const formMethods = useForm<CodesForm>({ defaultValues: data, resolver });
  const {
    handleSubmit,
    formState: { dirtyFields },
    setError,
  } = formMethods;

  console.log(formMethods.formState.errors);

  useEffect(() => {
    if (Object.keys(dirtyFields).length > 0) setStepShouldBlock(true);
  }, [dirtyFields]);

  const preSubmit = async (data: PartialNonConformitySchema) => {
    setIsLoading(true);

    try {
      const [elecData] = await Promise.all([
        data.electricityEan &&
          getConnectionInformation({ ean18: data.electricityEan }),
      ]);

      const hasInvalidElectricityEan =
        elecData && elecData.product === Product.Gas;

      if (hasInvalidElectricityEan)
        setError('electricityEan', {
          message: transtic('create.errors.ean_not_electricity'),
        });

      setIsLoading(false);

      if (hasInvalidElectricityEan) return;
    } catch (error) {
      // Do nothing, we'll just let the user continue

      console.warn(error);

      setIsLoading(false);
    }

    onSubmit(data);
  };

  return (
    <FormProvider {...formMethods}>
      <form onSubmit={handleSubmit(preSubmit)}>
        <Page>
          <PageHeader
            title={transtic('create.non_conformity.page_title')}
            previousText={t('common.steps.previous')}
            backHref="/berichtenbox/nieuw"
            details={stepper}
          />
          <Card className="mb-6 max-w-4xl p-6">
            <Stack gap="lg">
              <Stack gap="sm">
                <Text as="h2" variant="h5" className="text-content-heading">
                  {transtic('create.codes.enter_electricity_ean')}
                </Text>
              </Stack>
              <Stack gap="sm">
                <FormText
                  label={t('common.electricity')}
                  name="electricityEan"
                  fullWidth
                />
              </Stack>
              <Divider />
              <Stack direction="row" className="justify-between" gap="lg">
                {wizardActions}
              </Stack>
            </Stack>
          </Card>
        </Page>
      </form>
    </FormProvider>
  );
};
